/**
 * Date and time utility functions for consistent timestamp formatting
 * Converts UTC timestamps to local time for better user experience
 */

/**
 * Format timestamp to local date and time
 * @param timestamp - ISO timestamp string or Date object
 * @param options - Formatting options
 * @returns Formatted local date and time
 */
export const formatLocalDateTime = (timestamp: string | Date, options: Intl.DateTimeFormatOptions = {}): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };
  
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp to local date only
 * @param timestamp - ISO timestamp string or Date object
 * @param options - Formatting options
 * @returns Formatted local date
 */
export const formatLocalDate = (timestamp: string | Date, options: Intl.DateTimeFormatOptions = {}): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp to local time only
 * @param timestamp - ISO timestamp string or Date object
 * @param options - Formatting options
 * @returns Formatted local time
 */
export const formatLocalTime = (timestamp: string | Date, options: Intl.DateTimeFormatOptions = {}): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Time';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  
  return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
};

/**
 * Format timestamp for compact display (date + time on separate lines)
 * @param timestamp - ISO timestamp string or Date object
 * @returns Object with date and time strings
 */
export const formatCompactDateTime = (timestamp: string | Date): { date: string; time: string } => {
  if (!timestamp) return { date: 'N/A', time: 'N/A' };
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return { date: 'Invalid', time: 'Date' };
  
  return {
    date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
  };
};

/**
 * Format relative time (e.g., "2 hours ago", "Yesterday")
 * @param timestamp - ISO timestamp string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (timestamp: string | Date): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return formatLocalDate(timestamp);
  }
};

/**
 * Format timestamp for table display (compact format)
 * @param timestamp - ISO timestamp string or Date object
 * @returns Formatted timestamp for tables
 */
export const formatTableDateTime = (timestamp: string | Date): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();
  
  if (isToday) {
    return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else if (isYesterday) {
    return `Yesterday ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
  } else {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    });
  }
};

/**
 * Get user's timezone
 * @returns User's timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format timestamp with timezone info
 * @param timestamp - ISO timestamp string or Date object
 * @returns Formatted timestamp with timezone
 */
export const formatWithTimezone = (timestamp: string | Date): string => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  const timezone = getUserTimezone();
  return `${formatLocalDateTime(timestamp)} (${timezone})`;
};

/**
 * Check if timestamp is today
 * @param timestamp - ISO timestamp string or Date object
 * @returns True if timestamp is today
 */
export const isToday = (timestamp: string | Date): boolean => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const today = new Date();
  
  return date.toDateString() === today.toDateString();
};

/**
 * Check if timestamp is in the past
 * @param timestamp - ISO timestamp string or Date object
 * @returns True if timestamp is in the past
 */
export const isPast = (timestamp: string | Date): boolean => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const now = new Date();
  
  return date.getTime() < now.getTime();
};

/**
 * Check if timestamp is in the future
 * @param timestamp - ISO timestamp string or Date object
 * @returns True if timestamp is in the future
 */
export const isFuture = (timestamp: string | Date): boolean => {
  if (!timestamp) return false;
  
  const date = new Date(timestamp);
  const now = new Date();
  
  return date.getTime() > now.getTime();
};
